/**
 * BillingSuccess — post-checkout welcome page.
 *
 * Stripe redirects users here after a successful $14.99/mo subscription
 * via:
 *   https://dreamandbuildexams.com/billing/success?session_id={CHECKOUT_SESSION_ID}
 *
 * The webhook usually delivers + writes the DDB row in <5 seconds, so
 * we poll /billing/my-subscription on a 1.5s cadence until tier === 'pro'
 * (capped at 30 seconds). When the row arrives we flip to the celebratory
 * "active" state. If 30s elapses without the row, we render a graceful
 * "still processing" message — webhook will catch up eventually.
 *
 * Cleans the session_id off the URL once the page mounts so the secret
 * doesn't sit in the user's browser history.
 *
 * Props:
 *   onContinue() — called when the user clicks "Start practicing". Parent
 *                  resets URL state and re-renders cert-selection.
 */
import React, { useEffect, useState, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from './config';
import './BillingSuccess.css';

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_DURATION_MS = 30000;

export default function BillingSuccess({ onContinue }) {
  const [status, setStatus] = useState('activating'); // 'activating' | 'active' | 'pending'
  const [tier, setTier] = useState(null);
  const [email, setEmail] = useState('');
  const cancelledRef = useRef(false);

  // Strip the session_id query string off the URL on mount so it doesn't
  // sit in browser history. We don't actually use the session_id client-side
  // — the webhook is the source of truth for activation. Keeping the path
  // (/billing/success) is fine, just lose the secret query param.
  useEffect(() => {
    try {
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, '', cleanPath);
    } catch { /* ignore */ }
  }, []);

  // Poll /billing/my-subscription until the tier flips to 'pro' (or timeout).
  useEffect(() => {
    cancelledRef.current = false;
    const startedAt = Date.now();

    async function poll() {
      if (cancelledRef.current) return;
      try {
        const session = await fetchAuthSession().catch(() => null);
        const claims = session?.tokens?.idToken?.payload;
        const userEmail = claims?.email || '';
        const jwt = session?.tokens?.idToken?.toString() || '';
        if (userEmail && !cancelledRef.current) setEmail(userEmail);

        if (!jwt) {
          // Not signed in — they're probably visiting the URL directly without
          // having gone through Stripe. Just show the "still processing" view
          // so we never appear broken.
          if (!cancelledRef.current) setStatus('pending');
          return;
        }

        const r = await fetch(`${config.apiUrl}/billing/my-subscription`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (cancelledRef.current) return;
        if (r.ok) {
          const data = await r.json().catch(() => ({}));
          const t = data?.tier || 'free';
          // 'exam' is the direct-subscriber tier; 'team' from the cross-app
          // table also unlocks Pro UX. Anything else means the webhook
          // hasn't caught up yet — keep polling.
          if (t === 'exam' || t === 'team' || t === 'pro') {
            setTier(t === 'exam' || t === 'team' ? 'pro' : t);
            setStatus('active');
            return;
          }
        }
      } catch (err) {
        if (!cancelledRef.current) console.warn('[BillingSuccess] poll error:', err?.message);
      }

      // Continue polling unless we hit the cap.
      if (cancelledRef.current) return;
      if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
        setStatus('pending');
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    }

    poll();
    return () => { cancelledRef.current = true; };
  }, []);

  const handleContinue = () => {
    if (typeof onContinue === 'function') onContinue();
  };

  return (
    <div className="billing-success">
      <div className="billing-success-card">
        {status === 'activating' && (
          <>
            <div className="billing-success-spinner" aria-hidden="true">
              <span className="billing-success-spinner-ring" />
            </div>
            <h1 className="billing-success-title">Finalizing your subscription…</h1>
            <p className="billing-success-body">
              Stripe just confirmed your payment. We're activating your
              Pro access right now — usually takes a few seconds.
            </p>
            <p className="billing-success-help">
              You can leave this page open; we'll update automatically.
            </p>
          </>
        )}

        {status === 'active' && (
          <>
            <div className="billing-success-checkmark" aria-hidden="true">✓</div>
            <h1 className="billing-success-title">
              🎉 Welcome to Dream&amp;Build Exams Pro
            </h1>
            <p className="billing-success-body">
              {email
                ? <>Your subscription is active for <strong>{email}</strong>.</>
                : <>Your subscription is active.</>}
              {' '}Every cert in the catalog — Foundation, Associate, Professional,
              and Specialty — is now unlocked.
            </p>
            <p className="billing-success-help">
              You can manage or cancel your subscription any time from the
              billing portal in your account menu.
            </p>
            <button
              type="button"
              className="billing-success-cta"
              onClick={handleContinue}
            >
              Start practicing →
            </button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="billing-success-pending" aria-hidden="true">⏳</div>
            <h1 className="billing-success-title">Almost there…</h1>
            <p className="billing-success-body">
              Your payment was successful, but it's taking a bit longer than
              usual to finalize on our side. This is rare and your access
              will activate as soon as the webhook clears — usually within
              a minute or two.
            </p>
            <p className="billing-success-help">
              Refresh this page in a minute, or close it and come back later;
              your subscription is queued and will land. If it still hasn't
              activated after 10 minutes, email{' '}
              <a href="mailto:support@dreamandbuildllc.com">support@dreamandbuildllc.com</a>.
            </p>
            <button
              type="button"
              className="billing-success-cta"
              onClick={handleContinue}
            >
              Continue to exams
            </button>
          </>
        )}
      </div>
    </div>
  );
}
